import { Formik, useField } from "formik";
import {
  loginFormFields,
  loginInitialValues,
  loginValidationSchema,
} from "./loginSchema";
import styled from "styled-components";
import { primaryColor, white } from "../../shared/styles/color";
import { fontFamily } from "../../shared/styles/theme";
import CustomCheckbox from "../../shared/checkbox/CustomCheckbox";
import { useNavigate, useLocation } from "react-router-dom";
import { authenticate } from "../../../services/authenticate";
import { useDispatch } from "react-redux";
import { login } from "../../../redux/reducer/authReducer";
import { useState, useEffect } from "react";
import { ErrorMessage } from "../../shared/generic/headers";
import { useDeviceInfo } from "../../../helper/deviceInfo";
import { ButtonSpinner } from "../../shared/loading/LoadingScreen";
import { getHomeDashboardPath } from "../../../utils/dashboardRoutes";
import { needsSubscriptionOnboarding } from "../../../utils/subscriptionStatus";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { refreshStoredUserSession } from "../../../services/users/user-services";
import { FormAlert } from "../../shared/alerts/FormAlert";
import { SubscriptionComparisonTable } from "../../shared/sections/subscription-plan/SubscriptionComparisonTable";
import CaptchaField from "../../shared/inputs/CaptchaField";
import { FiEye, FiEyeOff, FiLock, FiMail, FiShield } from "react-icons/fi";
import { ErrorText } from "../../shared/inputs/ErrorText";

const LoginForm = ({ variant = "default" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [registrationAlert, setRegistrationAlert] = useState(null);
  const deviceInfo = useDeviceInfo();
  const [loading, setLoading] = useState(false);
  const [showPlanOnboarding, setShowPlanOnboarding] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaChallenge, setCaptchaChallenge] = useState({});
  const isAdmin = variant === "admin";

  useEffect(() => {
    if (isAdmin) return;
    if (location.state?.registrationSuccess) {
      setRegistrationAlert(location.state.registrationSuccess);
      return;
    }
    if (location.state?.onboardingSubscribe) {
      setShowPlanOnboarding(true);
    }
  }, [location.state, isAdmin]);

  if (showPlanOnboarding && !isAdmin) {
    return (
      <OnboardingContainer>
        <OnboardingCard>
          <OnboardingTitle>Choose your Liber plan</OnboardingTitle>
          <OnboardingText>
            Log in to continue, then pick the plan that fits your goals. You can start free and upgrade anytime.
          </OnboardingText>
          <ComparisonWrap>
            <SubscriptionComparisonTable monthly />
          </ComparisonWrap>
          <BtnRow>
            <SecondaryBtn type="button" onClick={() => setShowPlanOnboarding(false)}>
              Log in first
            </SecondaryBtn>
          </BtnRow>
        </OnboardingCard>
      </OnboardingContainer>
    );
  }

  return (
    <Card $compact={isAdmin}>
      <FormHeader>
        <FormTitle>{isAdmin ? "Admin Login" : "Log in to Liber"}</FormTitle>
        {isAdmin ? (
          <SignupPrompt>Sign in to manage the Liber platform.</SignupPrompt>
        ) : (
          <SignupPrompt>
            Don&apos;t have an account?{" "}
            <SignupLink type="button" onClick={() => navigate("/sign-up")}>
              Sign up
            </SignupLink>
          </SignupPrompt>
        )}
      </FormHeader>

      {registrationAlert && !isAdmin && (
        <FormAlert
          variant="success"
          title={registrationAlert.title}
          message={registrationAlert.message}
          onDismiss={() => setRegistrationAlert(null)}
        />
      )}

      <Formik
        initialValues={loginInitialValues}
        validationSchema={loginValidationSchema}
        onSubmit={async (values) => {
          if (!captchaValid) {
            setLoginErrorMessage("Please complete the security check.");
            return;
          }
          values.deviceId = deviceInfo?.fingerprintId;
          values.deviceName = deviceInfo?.deviceName;
          Object.assign(values, captchaChallenge);
          setLoading(true);
          const response = await authenticate(values);
          if (response?.status === 200) {
            const authUser = response?.data?.user;

            if (
              isAdmin &&
              String(authUser?.user_type || "").toLowerCase() !== "admin"
            ) {
              setLoading(false);
              setLoginErrorMessage(
                "This account does not have admin access.",
              );
              return;
            }

            setLoginErrorMessage("");
            dispatch(login(response?.data));
            await refreshStoredUserSession();
            const fresh = JSON.parse(
              localStorage.getItem("userData") || "null",
            );
            const user = fresh?.user ?? authUser;
            const userIsAdmin =
              String(user?.user_type || "").toLowerCase() === "admin";

            // Admin accounts always land in the admin console (never business/freelancer).
            if (isAdmin || userIsAdmin) {
              navigate("/admin", { replace: true });
              setLoading(false);
              return;
            }

            if (needsSubscriptionOnboarding(user) || location.state?.onboardingSubscribe) {
              navigate("/subscribe", { replace: true });
            } else {
              navigate(getHomeDashboardPath(user), { replace: true });
            }

            setLoading(false);
          } else {
            setLoading(false);
            setLoginErrorMessage(
              getApiErrorMessage(response, "Invalid email or password."),
            );
          }
        }}
      >
        {({ handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit} autoComplete="off">
            {loginFormFields.map((field) => (
              <IconLoginField
                key={field.name}
                name={field.name}
                type={field.type}
                placeholder={field.placeholder}
                icon={field.type === "password" ? FiLock : FiMail}
              />
            ))}

            <OptionsRow>
              <CustomCheckbox name="rememberMe" label="Remember me" />
              {!isAdmin ? (
                <ForgotPassword type="button" onClick={() => navigate("/forgot-password")}>
                  Forgot password?
                </ForgotPassword>
              ) : (
                <span />
              )}
            </OptionsRow>

            {loginErrorMessage && (
              <ErrorMessage>{loginErrorMessage}</ErrorMessage>
            )}

            <CaptchaField
              onValidatedChange={setCaptchaValid}
              onChallengeChange={setCaptchaChallenge}
            />

            <LoginButton type="submit" disabled={loading}>
              {loading ? <ButtonSpinner /> : isAdmin ? "Sign In" : "Log In"}
            </LoginButton>

            <SecurityNote>
              <FiShield size={16} color={primaryColor} />
              <span>
                Your information is safe with us. We{" "}
                <strong>never share</strong> your data.
              </span>
            </SecurityNote>
          </StyledForm>
        )}
      </Formik>
    </Card>
  );
};

function IconLoginField({ name, type, placeholder, icon: Icon }) {
  const [field, meta] = useField(name);
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  return (
    <FieldWrap>
      <InputShell>
        {Icon && (
          <LeadingIcon aria-hidden>
            <Icon size={18} />
          </LeadingIcon>
        )}
        <StyledInput
          {...field}
          type={inputType}
          placeholder={placeholder}
        />
        {isPassword && (
          <TogglePassword
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
          </TogglePassword>
        )}
      </InputShell>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FieldWrap>
  );
}

export default LoginForm;

const Card = styled.div`
  width: 100%;
  max-width: ${(p) => (p.$compact ? "420px" : "460px")};
  margin: 0 auto;
  padding: ${(p) => (p.$compact ? "28px 24px 24px" : "32px 28px 28px")};
  background: ${white};
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  text-align: left;

  @media (max-width: 768px) {
    padding: 24px 18px 20px;
    border-radius: 14px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 4px;
`;

const FormTitle = styled.h1`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1.3;
  color: #111827;
  margin: 0 0 8px;
`;

const SignupPrompt = styled.p`
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const SignupLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${primaryColor};
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const FieldWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputShell = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const LeadingIcon = styled.span`
  position: absolute;
  left: 14px;
  color: #9ca3af;
  display: inline-flex;
  pointer-events: none;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 13px 44px 13px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: ${fontFamily.font};
  font-weight: 400;
  line-height: 1.4;
  outline: none;
  box-sizing: border-box;
  background: ${white};
  color: #111827;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.12);
  }
`;

const TogglePassword = styled.button`
  position: absolute;
  right: 12px;
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: inline-flex;
  padding: 4px;

  &:hover {
    color: #4b5563;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`;

const ForgotPassword = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  color: ${primaryColor};
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  margin-top: 4px;
  font-size: 16px;
  height: 48px;
  border-radius: 10px;
  padding: 12px 16px;
  background-color: ${primaryColor};
  color: ${white};
  font-family: ${fontFamily.font};
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 4px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`;

const DividerText = styled.span`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
`;

const SocialRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: ${white};
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

const SocialIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const LinkedInIcon = styled.span`
  color: #0a66c2;
  display: flex;
  align-items: center;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${fontFamily.font};
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin-top: 2px;

  strong {
    color: ${primaryColor};
    font-weight: 600;
  }

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const OnboardingContainer = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem 1rem;
  box-sizing: border-box;
  max-width: 960px;
  margin: 0 auto;
`;

const OnboardingCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const OnboardingTitle = styled.h2`
  margin: 0 0 8px;
  text-align: center;
  color: #111827;
  font-family: ${fontFamily.font};
`;

const OnboardingText = styled.p`
  margin: 0 0 16px;
  text-align: center;
  color: #6b7280;
  line-height: 1.5;
  font-family: ${fontFamily.font};
`;

const ComparisonWrap = styled.div`
  overflow-x: auto;
  margin-bottom: 16px;
`;

const BtnRow = styled.div`
  display: flex;
  justify-content: center;
`;

const SecondaryBtn = styled.button`
  padding: 12px 20px;
  border: 1px solid ${primaryColor};
  border-radius: 999px;
  background: white;
  color: ${primaryColor};
  font-weight: 600;
  cursor: pointer;
  font-family: ${fontFamily.font};
`;
